import React, { useEffect, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trophy, Crown, Medal, Users, UserPlus, Store, Gift, Pencil, Trash2, MessageCircle } from "lucide-react";

const BADGE_CLS = {
  Diamond: "border-cyan-300/60 text-cyan-200 bg-cyan-400/10", Gold: "border-gold/70 text-gold bg-gold/10",
  Silver: "border-slate-300/60 text-slate-200 bg-slate-300/10", Bronze: "border-orange-400/60 text-orange-300 bg-orange-400/10",
  Rising: "border-emerald-400/40 text-emerald-300 bg-emerald-400/10",
};
const PODIUM = ["text-gold", "text-slate-200", "text-orange-300"];

const Avatar = ({ r, size = "w-12 h-12" }) => r.has_photo
  ? <img src={`${API}/members/${r.id}/photo`} alt={r.name} className={`${size} rounded-full object-cover ring-2 ring-gold/50`} />
  : <div className={`${size} rounded-full bg-primary/50 border border-gold/30 flex items-center justify-center font-serif text-gold`}>{r.name[0]}</div>;

export default function Leaderboard() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState({ rows: [], rules: {} });
  const [rewards, setRewards] = useState([]);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ reward: "", badge: "Gold" });

  const load = useCallback(async () => {
    const [lb, rw] = await Promise.all([api.get(`/leaderboard?month=${month}`), api.get(`/leaderboard/rewards?month=${month}`)]);
    setData(lb.data); setRewards(rw.data);
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const top = data.rows.slice(0, 3);
  const winners = data.rows.filter((r) => r.badge === "Diamond" || r.badge === "Gold");

  const monthLabel = new Date(month + "-01").toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const shareText = () => {
    const lines = [`*Bengal Umrah House — Member Leaderboard (${monthLabel})*`, ""];
    data.rows.slice(0, 10).forEach((r) => lines.push(`#${r.rank} ${r.name} — ${r.points} pts (${r.badge})`));
    const wr = rewards.filter((r) => r.month === month);
    if (wr.length) { lines.push("", "*Monthly Rewards:*"); wr.forEach((r) => lines.push(`🎁 ${r.member_name} (${r.badge}) — ${r.reward}`)); }
    lines.push("", "Earn points: customer +10 · recruit +15 · franchise sale +20. Join us and climb the board!");
    return lines.join("\n");
  };

  const save = async () => {
    try {
      if (edit.id) await api.put(`/leaderboard/rewards/${edit.id}`, { member_id: edit.member_id, ...form });
      else await api.post(`/leaderboard/rewards?month=${month}`, { member_id: edit.member_id, ...form });
      toast.success(edit.id ? "Reward updated" : "Reward added");
      setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => {
    await api.delete(`/leaderboard/rewards/${id}`);
    toast.success("Reward deleted"); load();
  };

  return (
    <div>
      <PageHeader eyebrow="Motivation" title="Member Leaderboard" subtitle="Monthly ranking of members by customers, recruits and franchise sales" />
      <div className="p-4 sm:p-8 space-y-6" data-testid="leaderboard-page">
        <div className="flex flex-wrap items-center gap-3">
          <Input type="month" data-testid="leaderboard-month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 bg-card/60" />
          <p className="text-xs text-muted-foreground">Points: customer +{data.rules.customer} · recruit +{data.rules.recruit} · franchise sale +{data.rules.franchise_sale}</p>
          <a data-testid="leaderboard-wa-share" href={`https://wa.me/?text=${encodeURIComponent(shareText())}`} target="_blank" rel="noopener noreferrer"
            className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/40 hover:bg-[#25D366]/25 transition-colors text-xs font-medium">
            <MessageCircle className="w-3.5 h-3.5" /> Post {monthLabel} board on WhatsApp
          </a>
        </div>

        <div className="grid sm:grid-cols-3 gap-4" data-testid="leaderboard-podium">
          {top.map((r, i) => (
            <Card key={r.id} className={`p-5 bg-card/40 border-gold/30 text-center ${i === 0 ? "sm:-mt-2 ring-1 ring-gold/50" : ""}`}>
              {i === 0 ? <Crown className="w-7 h-7 mx-auto text-gold" /> : <Medal className={`w-6 h-6 mx-auto ${PODIUM[i]}`} />}
              <div className="flex justify-center mt-2"><Avatar r={r} size="w-16 h-16" /></div>
              <p className="font-serif text-white font-semibold mt-2">{r.name}</p>
              <p className={`text-2xl font-mono mt-1 ${PODIUM[i]}`}>{r.points} pts</p>
              <Badge variant="outline" className={`mt-2 ${BADGE_CLS[r.badge]}`}>{r.badge} Partner</Badge>
            </Card>
          ))}
        </div>

        <Card className="p-4 bg-card/40 border-gold/30" data-testid="leaderboard-rewards">
          <p className="eyebrow text-gold mb-3 flex items-center gap-2"><Gift className="w-4 h-4" /> Monthly Winners &amp; Rewards — {monthLabel}</p>
          {winners.length === 0 && <p className="text-sm text-muted-foreground py-3 text-center">No Gold or Diamond partner this month yet.</p>}
          <div className="space-y-2">
            {winners.map((r) => {
              const rw = rewards.find((x) => x.member_id === r.id && x.month === month);
              return (
                <div key={r.id} data-testid={`reward-row-${r.id}`} className="flex flex-wrap items-center gap-3 rounded-lg border border-border/50 bg-card/30 px-3 py-2.5">
                  <Avatar r={r} size="w-9 h-9" />
                  <div className="min-w-0">
                    <p className="text-sm text-white">{r.name}</p>
                    <Badge variant="outline" className={`text-[10px] mt-0.5 ${BADGE_CLS[r.badge]}`}>{r.badge}</Badge>
                  </div>
                  <div className="flex-1 min-w-[180px] text-xs">
                    {rw ? <span className="text-gold" data-testid={`reward-text-${r.id}`}>{rw.reward}</span> : <span className="text-muted-foreground italic">No reward announced</span>}
                  </div>
                  {isOwner && (
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" variant="ghost" data-testid={`reward-edit-${r.id}`}
                        onClick={() => { setEdit({ id: rw?.id || null, member_id: r.id, name: r.name }); setForm({ reward: rw?.reward || "", badge: rw?.badge || r.badge }); }}
                        className="h-7 px-2 text-gold hover:bg-gold/10 text-xs gap-1"><Pencil className="w-3 h-3" /> {rw ? "Edit" : "Add reward"}</Button>
                      {rw && <Button size="sm" variant="ghost" data-testid={`reward-del-${r.id}`} onClick={() => del(rw.id)} className="h-7 px-2 text-destructive hover:bg-destructive/10 text-xs gap-1"><Trash2 className="w-3 h-3" /> Delete</Button>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-4 bg-card/40 border-gold/20" data-testid="leaderboard-table">
          {data.rows.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No members yet.</p>}
          {data.rows.map((r) => (
            <div key={r.id} data-testid={`leaderboard-row-${r.id}`} className="flex items-center gap-3 py-2.5 border-b border-border/40 last:border-0">
              <span className="w-8 text-center font-mono text-gold">#{r.rank}</span>
              <Avatar r={r} size="w-9 h-9" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{r.name}</p>
                <p className="text-[10px] text-muted-foreground capitalize">{r.role === "agent" ? "Associate Partner" : "Co-worker"}</p>
              </div>
              <span className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground w-20"><Users className="w-3 h-3 text-gold" /> {r.customers}</span>
              <span className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground w-20"><UserPlus className="w-3 h-3 text-gold" /> {r.recruits}</span>
              <span className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground w-20"><Store className="w-3 h-3 text-gold" /> {r.sales}</span>
              <Badge variant="outline" className={`text-[10px] ${BADGE_CLS[r.badge]}`}>{r.badge}</Badge>
              <span className="w-16 text-right font-mono text-gold text-sm flex items-center justify-end gap-1"><Trophy className="w-3 h-3" />{r.points}</span>
            </div>
          ))}
        </Card>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-[#052e22] border-gold/30 max-w-sm" data-testid="reward-dialog">
          <DialogHeader><DialogTitle className="font-serif text-gold">Reward — {edit?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Badge</Label>
              <select data-testid="reward-badge-select" value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })}
                className="w-full h-9 rounded-md bg-card/60 border border-input px-2 text-sm text-white">
                {["Diamond", "Gold", "Silver", "Bronze"].map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Reward (e.g. Free Umrah seat / Cash bonus ₹5,000)</Label>
              <Input data-testid="reward-text-input" value={form.reward} onChange={(e) => setForm({ ...form, reward: e.target.value })} className="bg-card/60" placeholder="Free Umrah seat" />
            </div>
            <Button data-testid="reward-save-btn" onClick={save} disabled={!form.reward.trim()} className="w-full bg-gold text-[#0a2e22] hover:bg-gold/90">Save reward</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
